import React, { useId, useState } from 'react';
import Accordion from '../../../../shared/components/Accordion/Accordion';
import Alert from '../../../../shared/components/Alert/Alert';
import Button from '../../../../shared/components/Button/Button';
import Card from '../../../../shared/components/Card/Card';
import Chip from '../../../../shared/components/Chip/Chip';
import Drawer from '../../../../shared/components/Drawer/Drawer';
import EmptyState from '../../../../shared/components/EmptyState/EmptyState';
import Input from '../../../../shared/components/Input/Input';
import Select from '../../../../shared/components/Select/Select';
import Typography from '../../../../shared/components/Typography/Typography';
import Dialog from '../../../../shared/components/Dialog/Dialog';
import erpLogo from '../../../../Icc_Logo.png';
import { buildRichToc, buildTocEntriesFromTiptapDocument } from '../wiki.richText';
import { exportWikiArticlePdf } from '../wikiPdfExport';
import { normalizeTiptapDocument } from '../services/wikiContentSerializer';
import { formatWikiDate } from '../wiki.date';
import WikiContentViewer from './viewer/WikiContentViewer';
import {
  EditIcon,
  InsertDriveFileIcon,
  PrintIcon,
  StarBorderIcon,
  StarIcon,
} from './WikiIcons';

const commentStatuses = {
  open: 'Ouverte',
  in_review: 'En traitement',
  resolved: 'Résolue',
  rejected: 'Rejetée',
};

const statusVariant = {
  Publie: 'success',
  Brouillon: 'neutral',
  'A valider': 'warning',
  'A réviser': 'danger',
  Archive: 'neutral',
};

const getAttachmentKey = (attachment, index) => {
  if (!attachment || typeof attachment !== 'object') {
    return `attachment-${String(attachment || index)}`;
  }

  return attachment.id
    || attachment.documentId
    || attachment.nextCloudPath
    || attachment.label
    || `attachment-${index}`;
};

const getAttachmentLabel = (attachment) => {
  if (!attachment || typeof attachment !== 'object') {
    return String(attachment || 'Document sans libellé');
  }

  return attachment.documentName
    || attachment.label
    || attachment.name
    || attachment.filename
    || attachment.nextCloudPath
    || (attachment.documentId ? `Document GED #${attachment.documentId}` : 'Document GED');
};

function WikiReaderPage({
  article,
  articleComments,
  articleRevisions,
  commentDraft,
  commentSaving,
  favorites,
  getCategoryLabel,
  onCommentDraftChange,
  onEdit,
  onOpenArticle,
  onSubmitComment,
  onUpdateComment,
  onToggleFavorite,
  relatedArticles,
}) {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [tocDrawerOpen, setTocDrawerOpen] = useState(false);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const tocDrawerTitleId = useId();
  const detailsDrawerTitleId = useId();

  if (!article) {
    return <Alert severity="warning">Article introuvable.</Alert>;
  }

  const attachments = Array.isArray(article.attachments) ? article.attachments : [];
  const comments = Array.isArray(articleComments) ? articleComments : [];
  const revisions = Array.isArray(articleRevisions) ? articleRevisions : [];
  const linkedArticles = Array.isArray(relatedArticles) ? relatedArticles : [];
  const favoriteIds = Array.isArray(favorites) ? favorites : [];
  const feedbackValue = String(commentDraft || '');
  const contentDocument = normalizeTiptapDocument(article.contentJson || article.plainText || article.content || '');
  const documentToc = buildTocEntriesFromTiptapDocument(contentDocument);
  const toc = documentToc.length
    ? documentToc
    : buildRichToc(article.renderedHtml || article.content || article.plainText || '');
  const isFavorite = favoriteIds.includes(article.id);
  const categoryLabel = getCategoryLabel(article.category);
  const submitFeedback = () => {
    onSubmitComment(article.id);
    setFeedbackOpen(false);
  };
  const renderToc = (onNavigate) => (
    <nav className="wiki-toc" aria-label="Sommaire de l'article">
      <strong>Sommaire</strong>
      {toc.map((item) => (
        <a key={item.id} className={`wiki-toc-link level-${item.level}`} href={`#${item.id}`} onClick={onNavigate}>
          {item.title}
        </a>
      ))}
    </nav>
  );
  const renderDocuments = () => (
    <section className="wiki-side-panel">
      <Typography variant="h6">Documents associés</Typography>
      {attachments.length ? attachments.map((attachment, index) => (
        attachment.nextCloudPath ? (
          <a key={getAttachmentKey(attachment, index)} href={attachment.openUrl || attachment.nextCloudPath} target="_blank" rel="noreferrer" className="wiki-document-link">
            <InsertDriveFileIcon fontSize="small" />
            <span>
              <strong>{getAttachmentLabel(attachment)}</strong>
              <small>{attachment.nextCloudPath}</small>
            </span>
          </a>
        ) : (
          <span key={getAttachmentKey(attachment, index)}>{getAttachmentLabel(attachment)}</span>
        )
      )) : (
        <EmptyState title="Aucun document associé">
        </EmptyState>
      )}
    </section>
  );
  const renderRelatedArticles = () => (
    <section className="wiki-side-panel">
      <Typography variant="h6">Articles liés</Typography>
      {linkedArticles.length ? linkedArticles.map((relatedArticle) => (
        <Card key={relatedArticle.id} className="wiki-related-card" compact interactive onClick={() => onOpenArticle(relatedArticle)}>
          <Chip size="small" variant="info">{relatedArticle.type}</Chip>
          <strong>{relatedArticle.title}</strong>
          <small>{getCategoryLabel(relatedArticle.category)}</small>
        </Card>
      )) : (
        <EmptyState title="Aucun article lié">
        </EmptyState>
      )}
    </section>
  );
  const renderRevisions = () => (
    <section className="wiki-revision-list">
      <Typography variant="h6">Historique</Typography>
      {revisions.length ? revisions.slice(0, 5).map((revision) => (
        <div key={revision.id} className="wiki-revision-row">
          <span>Version {revision.version} - {revision.status}</span>
          <small>{revision.changeSummary || 'Modification'} - {formatWikiDate(revision.createdAt)}</small>
        </div>
      )) : (
        <span>Aucune révision enregistrée.</span>
      )}
    </section>
  );
  const renderArticleInfo = () => (
    <section className="wiki-side-panel">
      <Typography variant="h6">Informations</Typography>
      <dl className="wiki-reader-info">
        <div>
          <dt>Version</dt>
          <dd>{article.version || '-'}</dd>
        </div>
        <div>
          <dt>Référent</dt>
          <dd>{article.owner || 'Non renseigné'}</dd>
        </div>
        <div>
          <dt>Mise à jour</dt>
          <dd>{formatWikiDate(article.updatedAt)}</dd>
        </div>
      </dl>
    </section>
  );
  const renderComments = () => (
    comments.length > 0 && (
      <Accordion title="Suivi des propositions" helper="Statut, attribution et lien revision">
        <div className="wiki-comment-list">
          {comments.slice(0, 8).map((comment) => (
            <div key={comment.id} className="wiki-comment-row">
              <div className="wiki-comment-main">
                <span>{comment.content}</span>
                <small>
                  {commentStatuses[comment.status] || comment.status || 'Ouverte'} - {formatWikiDate(comment.createdAt)}
                  {comment.resolvedAt ? ` - résolue le ${formatWikiDate(comment.resolvedAt)}` : ''}
                </small>
                {comment.resolutionNote && <small>Résolution : {comment.resolutionNote}</small>}
              </div>
              <div className="wiki-comment-controls">
                <Select
                  fullWidth
                  label="Statut"
                  value={comment.status || 'open'}
                  onChange={(event) => onUpdateComment(article.id, comment.id, { status: event.target.value })}
                  options={[
                    { value: 'open', label: 'Ouverte' },
                    { value: 'in_review', label: 'En traitement' },
                    { value: 'resolved', label: 'Résolue' },
                    { value: 'rejected', label: 'Rejetée' },
                  ]}
                />
                <Select
                  fullWidth
                  label="Révision"
                  placeholder="Aucune"
                  value={comment.revisionId || ''}
                  onChange={(event) => onUpdateComment(article.id, comment.id, { revisionId: event.target.value || null })}
                  options={revisions.map((revision) => ({ value: revision.id, label: `v${revision.version} - ${revision.status}` }))}
                />
                <Input
                  fullWidth
                  label="Assignée"
                  defaultValue={comment.assignedUserId || ''}
                  onBlur={(event) => onUpdateComment(article.id, comment.id, { assignedUserId: event.target.value || null })}
                  placeholder="ID utilisateur"
                />
                <Input
                  fullWidth
                  label="Note de résolution"
                  defaultValue={comment.resolutionNote || ''}
                  onBlur={(event) => onUpdateComment(article.id, comment.id, { resolutionNote: event.target.value })}
                  placeholder="Correction appliquée, rejet motivé..."
                />
              </div>
              <div className="wiki-row-actions">
                <Button size="small" variant="secondary" onClick={() => onUpdateComment(article.id, comment.id, { status: 'in_review' })}>Traiter</Button>
                <Button size="small" variant="secondary" onClick={() => onUpdateComment(article.id, comment.id, { status: 'resolved' })}>Resoudre</Button>
                <Button size="small" variant="danger" onClick={() => onUpdateComment(article.id, comment.id, { status: 'rejected' })}>Rejeter</Button>
              </div>
            </div>
          ))}
        </div>
      </Accordion>
    )
  );
  const renderAsideContent = () => (
    <div className="wiki-reader-aside-content">
      {toc.length > 0 && renderToc()}
      {renderDocuments()}
      {renderRelatedArticles()}
      {renderArticleInfo()}
      {renderRevisions()}
      {renderComments()}
    </div>
  );

  return (
    <article className="wiki-reader">
      <div className="wiki-breadcrumbs">Wiki &gt; {categoryLabel}</div>
      <div className="wiki-reader-header">
        <div>
          <Typography variant="h4">{article.title}</Typography>
          <div className="wiki-reader-meta">
            <Chip size="small" variant="info">{article.type}</Chip>
            <Chip size="small" variant="neutral">{categoryLabel}</Chip>
            <Chip size="small" variant={statusVariant[article.status] || 'neutral'}>{article.status}</Chip>
            <span>Version {article.version}</span>
            <span>Référent : {article.owner || 'Non renseigné'}</span>
            <span>Mise à jour le {formatWikiDate(article.updatedAt)}</span>
          </div>
        </div>
        <div className="wiki-reader-actions">
          {toc.length > 0 && (
            <Button className="wiki-reader-toc-button" variant="tertiary" onClick={() => setTocDrawerOpen(true)}>
              Sommaire
            </Button>
          )}
          <Button className="wiki-reader-details-button" variant="secondary" onClick={() => setDetailsDrawerOpen(true)}>
            Informations
          </Button>
          <Button variant="secondary" iconStart={<PrintIcon fontSize="small" />} onClick={() => exportWikiArticlePdf({ article, categoryLabel, logoUrl: erpLogo })}>
            PDF
          </Button>
          <Button variant="secondary" iconStart={isFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />} onClick={() => onToggleFavorite(article.id)}>
            Favori
          </Button>
          <Button variant="primary" iconStart={<EditIcon fontSize="small" />} onClick={() => onEdit(article)}>
            Modifier
          </Button>
        </div>
      </div>

      <div className="wiki-reader-layout">
        <div className="wiki-reader-main">
          <WikiContentViewer article={article} tocEntries={toc} />

          <section className="wiki-feedback-panel">
            <div className="wiki-section-header">
              <div>
                <Typography variant="h6">Une information semble incorrecte ?</Typography>
                <span>{comments.length} proposition(s) déjà envoyée(s)</span>
              </div>
              <Button variant="secondary" onClick={() => setFeedbackOpen(true)}>
                Proposer une correction
              </Button>
            </div>
          </section>
        </div>

        <aside className="wiki-reader-aside" aria-label="Informations secondaires de l'article">
          {renderAsideContent()}
        </aside>
      </div>

      <Drawer
        labelledBy={tocDrawerTitleId}
        open={tocDrawerOpen}
        placement="right"
        title="Sommaire"
        onClose={() => setTocDrawerOpen(false)}
      >
        {renderToc(() => setTocDrawerOpen(false))}
      </Drawer>

      <Drawer
        labelledBy={detailsDrawerTitleId}
        open={detailsDrawerOpen}
        placement="right"
        title="Informations de l'article"
        onClose={() => setDetailsDrawerOpen(false)}
      >
        <div className="wiki-reader-details-drawer">
          {renderAsideContent()}
        </div>
      </Drawer>

      <Dialog
        actions={(
          <>
            <Button variant="tertiary" onClick={() => setFeedbackOpen(false)} disabled={commentSaving}>
              Annuler
            </Button>
            <Button variant="primary" disabled={!feedbackValue.trim() || commentSaving} loading={commentSaving} onClick={submitFeedback}>
              {commentSaving ? 'Envoi...' : 'Envoyer'}
            </Button>
          </>
        )}
        describedBy="wiki-feedback-description"
        open={feedbackOpen}
        size="small"
        title="Proposer une correction"
        onClose={() => setFeedbackOpen(false)}
      >
        <p id="wiki-feedback-description" className="wiki-dialog-help">
          Décrivez l'information à corriger ou le complément à apporter. La proposition sera suivie par l'equipe Wiki.
        </p>
        <Input
          fullWidth
          label="Correction proposée"
          value={feedbackValue}
          onChange={(event) => onCommentDraftChange(event.target.value)}
          placeholder="Décrivez la correction ou le complément à apporter..."
          multiline
          rows={4}
        />
      </Dialog>
    </article>
  );
}

export default WikiReaderPage;
